// Define interfaces for the input data
interface Team {
    id: string;
    name: string;
    image_url: string;
}

interface Skill {
    si_id: string;
    name: string;
}

interface Player {
    player_id: string;
    "player.display_name": string;
    "player.skill_id": string;
    player_is_captain: boolean;
    player_is_keeper: boolean;
}

interface Squad {
    team_id: string;
    result: Player[];
}

interface Style {
    player_id: string;
    batting_style_text?: string;
    bowling_style_text?: string;
    nationality_id?: string;
    nationality?: string;
    nationality_short_code?: string;
}

interface Data {
    team_data: Team[];
    skill_data: Skill[];
    team_player_squad: Squad[];
    style_data: Style[];
}

// Define interfaces for the transformed output
interface TransformedPlayer {
    player_id: string;
    player_name: string;
    is_indian: boolean;
    team_name: string;
    team_flag: string;
    player_skill: string;
    player_is_captain: boolean;
    player_is_keeper: boolean;
    batting_style_text: string;
    bowling_style_text: string;
    player_nationality: string; // New key added
}

export interface TransformedTeam {
    team_name: string;
    team_image: string;
    playerlist: TransformedPlayer[];
}

// The transformation function
export function transformTeamsPlayers(data: Data): TransformedTeam[] {
    const { team_data, skill_data, team_player_squad, style_data } = data;

    // Build mapping from team id to team details
    const teamMap: { [key: string]: Team } = team_data.reduce((acc, team) => {
        acc[team.id] = team;
        return acc;
    }, {} as { [key: string]: Team });

    // Build mapping from skill id (si_id) to human-readable skill name using skill_data
    const skillMap: { [key: string]: string } = skill_data.reduce(
        (acc, skill) => {
            if (skill.si_id) {
                //@ts-ignore
                acc[skill.si_id] = skill.id;
            }
            return acc;
        },
        {} as { [key: string]: string },
    );

    // Build mapping from player_id to style info from style_data.
    // If there are duplicate entries for a player, we take the first occurrence.
    const styleMap: { [playerId: string]: Style } = style_data.reduce(
        (acc, style) => {
            if (!acc[style.player_id]) {
                acc[style.player_id] = style;
            }
            return acc;
        },
        {} as { [playerId: string]: Style },
    );

    // Object to accumulate teams and their unique players
    const teams: {
        [teamId: string]: {
            team_name: string;
            team_image: string;
            playerlist: { [playerId: string]: TransformedPlayer };
        };
    } = {};

    team_player_squad.forEach((squad) => {
        const teamId = squad.team_id;
        if (!teams[teamId]) {
            const teamInfo: any = teamMap[teamId] || {
                id: teamId,
                name: teamId,
                image_url: "",
            };
            teams[teamId] = {
                // Using short_name from teamInfo if available (otherwise use name)
                team_name: teamInfo.short_name,
                //@ts-ignore
                team_full_name: teamInfo.name,
                team_image: teamInfo.image_url,
                playerlist: {}, // use an object for uniqueness per player
            };
        }

        squad.result.forEach((player) => {
            const playerId = player.player_id;
            if (!teams[teamId].playerlist[playerId]) {
                // Fetch style info for this player based on player id
                const styleInfo: any = styleMap[playerId] || {};
                let battingStyle = "";
                let bowlingStyle = "";
                const playerName = player["player.display_name"];

                const isIndian =
                    styleInfo.nationality?.toLowerCase() === "india";
                // Get the player's skill id and then the human-readable name
                const skillId = player["player.skill_id"];
                const playerSkill = skillMap[skillId] || skillId;

                // Determine which style(s) to include based on skill id:
                // Batsman (1) and Wicket-Keeper (4): only batting style
                // Bowler (2): only bowling style
                // All-Rounder (3): both batting and bowling styles
                if (skillId === "1" || skillId === "4") {
                    battingStyle = styleInfo.batting_style_text || "";
                } else if (skillId === "2") {
                    bowlingStyle = styleInfo.bowling_style_text || "";
                } else if (skillId === "3") {
                    battingStyle = styleInfo.batting_style_text || "";
                    bowlingStyle = styleInfo.bowling_style_text || "";
                }

                teams[teamId].playerlist[playerId] = {
                    player_id: playerId,
                    player_name: playerName,
                    is_indian: isIndian,
                    team_name: teams[teamId].team_name,
                    team_flag: teams[teamId].team_image,
                    player_skill: playerSkill,
                    player_is_captain: player.player_is_captain,
                    player_is_keeper: player.player_is_keeper,
                    batting_style_text: battingStyle,
                    bowling_style_text: bowlingStyle,
                    player_nationality: styleInfo.nationality_short_code || "",
                };
            }
        });
    });

    // Convert teams object to an array; convert each team's playerlist object to an array as well.
    return Object.values(teams).map((team) => ({
        team_name: team.team_name,
        team_image: team.team_image,
        playerlist: Object.values(team.playerlist),
    }));
}
